import Vue from 'vue'
import {ModalPlugin, ToastPlugin} from 'bootstrap-vue'
import VueCompositionAPI from '@vue/composition-api'
import VueTheMask from 'vue-the-mask'
import MoneySpinner from 'v-money-spinner'
import * as Sentry from "@sentry/vue"

import router from './router'
import store from './store'
import App from './App.vue'

// Global Components
import './global-components'

// 3rd party plugins
import '@axios'
import '@/libs/acl'
import { planUpgradeMixin } from '@/libs/acl/planUpgrade'

// Mixin global para upgrade de plano
Vue.mixin(planUpgradeMixin)
import '@/libs/toastification'
import '@/libs/sweet-alerts'
import '@/libs/vue-select'
import '@/libs/tour'
import '@/libs/vee-validate'

Vue.use(MoneySpinner)
Vue.use(VueTheMask)

// BSV Plugin Registration
Vue.use(ToastPlugin)
Vue.use(ModalPlugin)

// Composition API
Vue.use(VueCompositionAPI)

// import core styles
import '@core/scss/core.scss'

// import assets styles
import '@/assets/scss/style.scss'

Vue.config.productionTip = false


// Mensagens de falha de import() de chunk antigo após um novo deploy (cache de
// assets). Tratadas com reload automático abaixo — não precisam ir ao Sentry.
const isDynamicImportErrorMessage = (message) => {
    if (!message || typeof message !== 'string') return false
    return (
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Unable to preload CSS')
    )
}

Sentry.init({
    Vue,
    dsn: "https://3d0f8ca08202a761cd214482d6bc41fb@o4508795680063488.ingest.us.sentry.io/4508795694088192",
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost", "http:\/\/127.0.0.1:8000", /^https:\/\/veltacar\.com\.br/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Filtra ruído de erros benignos/esperados que chegam como "unhandledrejection"
    // e não representam bugs reais. Erros de servidor (5xx) continuam sendo reportados.
    beforeSend(event, hint) {
        const error = hint && hint.originalException
        const message =
            (error && typeof error.message === 'string' && error.message) ||
            (typeof event.message === 'string' && event.message) ||
            ''

        // Vue Router: navegação redundante/redirecionada (benigna). VELTACAR-VUE-3/E/N
        if (
            (error && (error._isRouter || error.name === 'NavigationDuplicated')) ||
            message.includes('Avoided redundant navigation') ||
            message.includes('Redirected when going from') ||
            message.includes('navigation guard')
        ) {
            return null
        }

        // Chunk dinâmico antigo após deploy: a aplicação recarrega sozinha. VELTACAR-VUE-V
        if (isDynamicImportErrorMessage(message)) {
            return null
        }

        // axios sem resposta (erro de rede/conexão): já avisamos o usuário. VELTACAR-VUE-M
        if (error && error.isAxiosError && !error.response) {
            return null
        }

        // Respostas HTTP esperadas do backend (validação/permissão/CSRF/rate-limit):
        // ruído quando viram unhandledrejection. Mantemos 5xx (erros reais). VELTACAR-VUE-1
        if (
            error &&
            error.isAxiosError &&
            error.response &&
            [400, 401, 403, 409, 419, 422, 429].includes(error.response.status)
        ) {
            return null
        }

        return event
    },
});

Vue.directive('can', {
    inserted(el, binding, vnode, old) {
        const permissoes = store.getters['permissoes/getPermissoes'];
        const permissao_sigla = binding.value[0];
        const permissao_acao = binding.value[1];

        if (!permissoes['ADMIN'] && (typeof permissoes[permissao_sigla] == 'undefined' || typeof permissoes[permissao_sigla][permissao_acao] == 'undefined' || !permissoes[permissao_sigla][permissao_acao])) {
            vnode.elm.parentElement.removeChild(vnode.elm)
        }
    }
})

// Após um novo deploy, o usuário pode estar com o HTML/app antigo em cache e
// tentar importar um chunk Vite (`/build/assets/*-<hash>.js`) que não existe mais.
// Nesses casos recarregamos a página uma única vez (flag em sessionStorage para
// evitar loop) para buscar o manifest novo. (Sentry VELTACAR-VUE-V)
const CHUNK_RELOAD_KEY = 'velta:chunk-reload'

const reloadOnceForStaleChunk = () => {
    try {
        if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
        window.location.reload()
    } catch (e) {
        // sessionStorage indisponível (modo privado/in-app): evita recarregar em loop
    }
}

window.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason
    const message = typeof reason === 'string' ? reason : (reason && reason.message) || ''
    if (isDynamicImportErrorMessage(message)) {
        reloadOnceForStaleChunk()
    }
})

window.addEventListener('error', (event) => {
    const message = (event && event.message) || ''
    if (isDynamicImportErrorMessage(message)) {
        reloadOnceForStaleChunk()
    }
})

new Vue({
    router,
    store,
    render: h => h(App),
}).$mount('#app')
